export type GlassColor = 'crystal' | 'ocean' | 'sunset' | 'forest' | 'royal';

export interface GlassStyle {
  background: string;
  borderColor: string;
  shadowColor: string;
}

export const glassColorStyles: Record<GlassColor, GlassStyle> = {
  crystal: {
    background: 'rgba(219, 234, 254, 0.7)',
    borderColor: 'rgba(147, 197, 253, 0.5)',
    shadowColor: 'rgba(59, 130, 246, 0.1)'
  },
  ocean: {
    background: 'rgba(59, 130, 246, 0.15)',
    borderColor: 'rgba(59, 130, 246, 0.25)',
    shadowColor: 'rgba(59, 130, 246, 0.15)'
  },
  sunset: {
    background: 'rgba(251, 146, 60, 0.15)',
    borderColor: 'rgba(251, 146, 60, 0.25)',
    shadowColor: 'rgba(251, 146, 60, 0.15)'
  },
  forest: {
    background: 'rgba(34, 197, 94, 0.15)',
    borderColor: 'rgba(34, 197, 94, 0.25)',
    shadowColor: 'rgba(34, 197, 94, 0.15)'
  },
  royal: {
    background: 'rgba(139, 92, 246, 0.15)',
    borderColor: 'rgba(139, 92, 246, 0.25)',
    shadowColor: 'rgba(139, 92, 246, 0.15)'
  }
};

export const glassColorStylesDark: Record<GlassColor, GlassStyle> = {
  crystal: {
    background: 'rgba(30, 58, 138, 0.8)',
    borderColor: 'rgba(59, 130, 246, 0.5)',
    shadowColor: 'rgba(29, 78, 216, 0.3)'
  },
  ocean: {
    background: 'rgba(30, 58, 138, 0.8)',
    borderColor: 'rgba(59, 130, 246, 0.5)',
    shadowColor: 'rgba(59, 130, 246, 0.2)'
  },
  sunset: {
    background: 'rgba(124, 45, 18, 0.8)',
    borderColor: 'rgba(251, 146, 60, 0.5)',
    shadowColor: 'rgba(251, 146, 60, 0.2)'
  },
  forest: {
    background: 'rgba(20, 83, 45, 0.8)',
    borderColor: 'rgba(34, 197, 94, 0.5)',
    shadowColor: 'rgba(34, 197, 94, 0.2)'
  },
  royal: {
    background: 'rgba(76, 29, 149, 0.8)',
    borderColor: 'rgba(139, 92, 246, 0.5)',
    shadowColor: 'rgba(139, 92, 246, 0.2)'
  }
};

export function getGlassStyles(color: GlassColor = 'crystal', isDark: boolean = false): string {
  const styles = isDark ? glassColorStylesDark[color] : glassColorStyles[color];
  
  return `
    background: ${styles.background};
    border: 1px solid ${styles.borderColor};
    box-shadow: 0 8px 32px 0 ${styles.shadowColor};
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  `;
}

export function getGlassClasses(color: GlassColor = 'crystal', isDark: boolean = false): string {
  const baseClasses = 'backdrop-blur-md';
  
  const colorClasses: Record<GlassColor, { light: string; dark: string }> = {
    crystal: {
      light: 'bg-blue-100/70 border-blue-200/50 shadow-blue-500/10',
      dark: 'bg-blue-950/80 border-blue-500/50 shadow-blue-700/30'
    },
    ocean: {
      light: 'bg-blue-500/15 border-blue-500/25 shadow-blue-500/15',
      dark: 'bg-blue-900/80 border-blue-500/50 shadow-blue-500/20'
    },
    sunset: {
      light: 'bg-orange-400/15 border-orange-400/25 shadow-orange-400/15',
      dark: 'bg-orange-900/80 border-orange-400/50 shadow-orange-400/20'
    },
    forest: {
      light: 'bg-green-500/15 border-green-500/25 shadow-green-500/15',
      dark: 'bg-green-900/80 border-green-500/50 shadow-green-500/20'
    },
    royal: {
      light: 'bg-purple-500/15 border-purple-500/25 shadow-purple-500/15',
      dark: 'bg-purple-900/80 border-purple-500/50 shadow-purple-500/20'
    }
  };
  
  const selectedClasses = colorClasses[color];
  return `${baseClasses} ${isDark ? selectedClasses.dark : selectedClasses.light}`;
}